// Pure decision and formatting logic for the Accounts page, kept out of
// accounts.ts so it is unit-testable without a DOM (see
// accounts-support.test.ts). Nothing here may value-import another project
// module: node --test cannot resolve the project's extension-less imports.

import type {
  AccountLastSignInMethod,
  AccountListParams,
  AccountSignInFilter,
  AccountSignInMethod,
  AccountSortField,
  AccountSortOrder,
  AdminAccountOrganization,
} from '../api/accounts';

export const SEARCH_MAX_LENGTH = 200;

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export function signInLabel(method: AccountSignInMethod): string {
  switch (method) {
    case 'apple':
      return 'Apple';
    case 'email':
      return 'Email link';
    default:
      // 'other' and anything a newer API adds: never print an unknown raw value.
      return 'Other';
  }
}

/** How the newest session was established; null when no session is on record. */
export function lastSignInLabel(method: AccountLastSignInMethod | null): string | null {
  if (method === null) return null;
  switch (method) {
    case 'passkey':
      return 'Passkey';
    case 'apple':
      return 'Apple';
    case 'email':
      return 'Email link';
    default:
      return 'Other';
  }
}

const RELAY_DOMAIN = 'privaterelay.appleid.com';

/**
 * Sign in with Apple "Hide My Email" addresses. Compares the whole domain, so
 * lookalikes such as `privaterelay.appleid.com.evil.example` do not match.
 */
export function isPrivateRelay(email: string | null | undefined): boolean {
  if (!email) return false;
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  return email.slice(at + 1).trim().toLowerCase() === RELAY_DOMAIN;
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/**
 * "5 min ago". Null for a missing or unparseable time, so the caller picks the
 * wording for that state instead of showing "NaN days ago". `now` is a
 * parameter so tests do not depend on the clock.
 */
export function relativeAgo(
  value: string | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!value) return null;
  const time = Date.parse(value);
  if (Number.isNaN(time)) return null;
  const diff = now - time;
  // Includes a time slightly in the future: clock skew between server and browser.
  if (diff < MIN) return 'just now';
  if (diff < HOUR) return `${Math.floor(diff / MIN)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} hr ago`;
  const days = Math.floor(diff / DAY);
  if (days < 30) return `${plural(days, 'day')} ago`;
  // 360-364 days would floor to 12 months, which reads as a year before it is.
  if (days < 365) return `${Math.min(11, Math.floor(days / 30))} mo ago`;
  return `${Math.floor(days / 365)} yr ago`;
}

export interface LastSeenDisplay {
  text: string;
  tooltip: string;
}

/**
 * What the Last seen cell shows. lastSeenAt is the newest session token, which
 * only reaches back about 30 days and is cleared by sign-out, so a missing
 * value means "no recent session", never "never signed in".
 */
export function lastSeenDisplay(
  value: string | null | undefined,
  now: number = Date.now(),
  timeZone?: string,
): LastSeenDisplay {
  const ago = relativeAgo(value, now);
  if (ago === null) return { text: '—', tooltip: 'No recent session' };
  return { text: ago, tooltip: exactTime(value, timeZone) };
}

/**
 * Some ICU builds (Safari among them) put U+202F, a narrow no-break space,
 * before AM/PM. It is invisible, does not match a plain space when copied into
 * a search or a ticket, and is easy to miss in a diff, so write it as an escape.
 */
export function plainSpaces(text: string): string {
  return text.replace(/\u202f/g, ' ');
}

/**
 * Exact local time with seconds and the zone name, for tooltips. `timeZone`
 * exists for tests; the browser's own zone is used otherwise.
 */
export function exactTime(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return plainSpaces(
    date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      ...(timeZone ? { timeZone } : {}),
    }),
  );
}

const PRODUCT_PREFIX = 'com.checkidapp.';
const PRODUCT_NAMES: Record<string, string> = {
  pro: 'Pro',
  'business.monthly': 'Teams monthly',
  'business.yearly': 'Teams yearly',
};

export function productShortName(productId: string | null): string {
  if (!productId) return 'Unknown product';
  const tail = productId.startsWith(PRODUCT_PREFIX)
    ? productId.slice(PRODUCT_PREFIX.length)
    : productId;
  return PRODUCT_NAMES[tail] ?? tail;
}

export function subscriptionStatusLabel(status: string | null): string {
  if (!status) return 'status unknown';
  return status.replace(/_/g, ' ');
}

export type SubscriptionTone = 'ok' | 'warn' | 'danger' | 'neutral' | 'unknown';

/** Green means the subscription currently entitles the account, nothing else. */
export function subscriptionTone(status: string | null): SubscriptionTone {
  switch (status) {
    case null:
    case '':
      return 'unknown';
    case 'active':
    case 'in_trial':
      return 'ok';
    case 'grace_period':
      return 'warn';
    case 'revoked':
      return 'danger';
    default:
      return 'neutral';
  }
}

// Statuses are typed `unknown` here although the contract says string: one row
// with a missing or odd status must render muted, not throw and replace the
// whole list with the error banner.

/** Exact match on the API's lowercase value; case-folding would accept look-alikes. */
export function membershipIsActive(status: unknown): boolean {
  return status === 'active';
}

function membershipStatusText(status: unknown): string {
  return typeof status === 'string' && status !== '' ? status : 'unknown';
}

function sentenceCase(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * "Acme Bar · Owner", with the status spelled out when it is not active. The
 * status is shown as sent, so an unknown value stays visible instead of being
 * mistaken for active; an empty or missing one reads "unknown".
 */
export function membershipLabel(
  org: Pick<AdminAccountOrganization, 'name' | 'role'> & { status?: unknown },
): string {
  const base = `${org.name} · ${sentenceCase(org.role)}`;
  return membershipIsActive(org.status) ? base : `${base} (${membershipStatusText(org.status)})`;
}

export interface AccountsQueryState {
  search: string;
  signIn: AccountSignInFilter | null;
  sort: AccountSortField;
  order: AccountSortOrder;
  page: number;
}

export const DEFAULT_ACCOUNTS_STATE: AccountsQueryState = {
  search: '',
  signIn: null,
  sort: 'createdAt',
  order: 'desc',
  page: 1,
};

export interface SortOption {
  value: string;
  label: string;
  sort: AccountSortField;
  order: AccountSortOrder;
}

export const SORT_OPTIONS: SortOption[] = [
  { value: 'createdAt:desc', label: 'Newest', sort: 'createdAt', order: 'desc' },
  { value: 'createdAt:asc', label: 'Oldest', sort: 'createdAt', order: 'asc' },
  { value: 'lastSeen:desc', label: 'Recently seen', sort: 'lastSeen', order: 'desc' },
];

export function sortOptionValue(state: Pick<AccountsQueryState, 'sort' | 'order'>): string {
  return `${state.sort}:${state.order}`;
}

/** Changing the sort restarts at page 1 and keeps the search and filter. */
export function applySortOption(state: AccountsQueryState, value: string): AccountsQueryState {
  const option = SORT_OPTIONS.find((o) => o.value === value);
  if (!option) return state;
  return { ...state, sort: option.sort, order: option.order, page: 1 };
}

/**
 * The typed search as the page applies it: trimmed and capped as the URL
 * parser does. Null when it would not change the search; a new term restarts at
 * page 1.
 */
export function applySearchTerm(state: AccountsQueryState, raw: string): AccountsQueryState | null {
  const search = raw.trim().slice(0, SEARCH_MAX_LENGTH);
  return search === state.search ? null : { ...state, search, page: 1 };
}

/** The sign-in filter from its select; anything but apple or email means all. Restarts at page 1. */
export function applySignInFilter(state: AccountsQueryState, value: string): AccountsQueryState {
  return { ...state, signIn: value === 'apple' || value === 'email' ? value : null, page: 1 };
}

/**
 * A page click. `pending` is the state with any search still waiting for its
 * pause folded in. The page number belongs to the list on screen, so it only
 * holds while the term is unchanged; a newer term restarts at page 1 of it.
 */
export function applyPageClick(
  state: AccountsQueryState,
  pending: AccountsQueryState,
  page: number,
): AccountsQueryState {
  return pending.search === state.search ? { ...state, page } : pending;
}

/**
 * Reads the page's state from the URL. Anything the API would reject (it
 * answers 400 for unknown enum values) falls back to the default instead.
 */
export function parseAccountsQuery(query: URLSearchParams): AccountsQueryState {
  const signIn = query.get('signIn');
  const sort: AccountSortField = query.get('sort') === 'lastSeen' ? 'lastSeen' : 'createdAt';
  // The sort control only offers "Recently seen" newest-first, so a hand-edited
  // URL must not select an order the control cannot show.
  const order: AccountSortOrder =
    sort === 'createdAt' && query.get('order') === 'asc' ? 'asc' : 'desc';
  const rawPage = query.get('page') ?? '';
  const page = /^[1-9]\d*$/.test(rawPage) && Number.isSafeInteger(Number(rawPage)) ? Number(rawPage) : 1;
  return {
    search: (query.get('search') ?? '').trim().slice(0, SEARCH_MAX_LENGTH),
    signIn: signIn === 'apple' || signIn === 'email' ? signIn : null,
    sort,
    order,
    page,
  };
}

/** The page's own URL: only what differs from the defaults, in a stable order. */
export function accountsPath(state: AccountsQueryState): string {
  const usp = new URLSearchParams();
  if (state.search) usp.set('search', state.search);
  if (state.signIn) usp.set('signIn', state.signIn);
  if (state.sort !== DEFAULT_ACCOUNTS_STATE.sort) usp.set('sort', state.sort);
  if (state.order !== DEFAULT_ACCOUNTS_STATE.order) usp.set('order', state.order);
  if (state.page !== DEFAULT_ACCOUNTS_STATE.page) usp.set('page', String(state.page));
  const qs = usp.toString();
  return qs ? `/accounts?${qs}` : '/accounts';
}

export function accountsListParams(state: AccountsQueryState, limit: number): AccountListParams {
  return {
    ...(state.search ? { search: state.search } : {}),
    ...(state.signIn ? { signIn: state.signIn } : {}),
    sort: state.sort,
    order: state.order,
    page: state.page,
    limit,
  };
}

function isFiltered(state: AccountsQueryState): boolean {
  return state.search !== '' || state.signIn !== null;
}

export function emptyMessage(state: AccountsQueryState): string {
  return isFiltered(state) ? 'No accounts match this search.' : 'No accounts yet.';
}

export function totalLabel(total: number, state: AccountsQueryState): string {
  const count = total.toLocaleString('en-US');
  const noun = total === 1 ? 'account' : 'accounts';
  return isFiltered(state) ? `${count} matching ${noun}` : `${count} ${noun}`;
}
